import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, KeyRound, User } from "lucide-react";

export default function ClientsAuthEditor() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/clients-auth").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.email) { toast.error("Email obrigatório"); return; }
    try {
      if (editing) {
        await api.put(`/admin/clients-auth/${editing.id}`, form);
        toast.success("Cliente atualizado.");
      } else {
        if (!form.password) { toast.error("Defina uma senha inicial."); return; }
        await api.post("/admin/clients-auth", form);
        toast.success("Cliente criado.");
      }
      setForm({}); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
  };

  const startEdit = (c) => { setEditing(c); setForm({ ...c, password: "", new_password: "" }); };
  const cancel = () => { setEditing(null); setForm({}); };

  const del = async (c) => {
    if (!window.confirm(`Excluir cliente ${c.email}?`)) return;
    await api.delete(`/admin/clients-auth/${c.id}`);
    toast.success("Removido.");
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl">Clientes (Área do Cliente)</h1>
          <p className="text-sm text-neutral-500 mt-1">Gerencie contas de acesso ao portal. Senha inicial define o login por email/senha; o cliente também pode entrar via Google.</p>
        </div>
      </div>

      <section className="border border-black/10 rounded-md p-5 mb-6">
        <div className="font-display text-xl mb-4 flex items-center gap-2">
          {editing ? <><User className="w-4 h-4 text-accent-orange" /> Editando: {editing.email}</> : <><Plus className="w-4 h-4" /> Novo Cliente</>}
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <F label="Email *"><input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-form-email" className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></F>
          <F label="Nome"><input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-form-name" className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></F>
          <F label="Empresa (Incorporadora)"><input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="client-form-company" className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></F>
          <F label="Telefone"><input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></F>
          <F label="Atendimento Responsável"><input value={form.responsible_am || ""} onChange={(e) => setForm({ ...form, responsible_am: e.target.value })} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></F>
          <F label="Cliente desde (data ISO)"><input type="date" value={form.client_since ? form.client_since.slice(0, 10) : ""} onChange={(e) => setForm({ ...form, client_since: e.target.value })} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></F>
          <F label={editing ? "Nova senha (deixe em branco para manter)" : "Senha inicial *"}>
            <input type="password" value={form[editing ? "new_password" : "password"] || ""} onChange={(e) => setForm({ ...form, [editing ? "new_password" : "password"]: e.target.value })} data-testid="client-form-password" className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
          </F>
          <F label="Ativo">
            <select value={form.is_active === false ? "0" : "1"} onChange={(e) => setForm({ ...form, is_active: e.target.value === "1" })} className="w-full border border-black/15 px-3 py-2 rounded text-sm">
              <option value="1">Sim</option><option value="0">Pausado</option>
            </select>
          </F>
        </div>
        <div className="mt-4 pt-4 border-t border-black/10">
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">Seções habilitadas no portal deste cliente</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              { key: "archive", label: "Arquivo de Entregas", default: true },
              { key: "orulo", label: "Radar Órulo", default: false },
              { key: "fipezap", label: "Radar FipeZap", default: true },
              { key: "ibge", label: "IBGE Demográfico", default: true },
              { key: "talkie", label: "Pesquisas Talkie", default: false },
              { key: "hector", label: "Bot Hector", default: true },
              { key: "nps", label: "Pesquisas NPS", default: true },
            ].map((f) => {
              const current = (form.features || {})[f.key];
              const enabled = current === undefined ? f.default : current;
              return (
                <label key={f.key} className="flex items-center gap-2 border border-black/10 rounded p-2 cursor-pointer hover:bg-black/5" data-testid={`client-feature-${f.key}`}>
                  <input type="checkbox" checked={!!enabled} onChange={(e) => setForm({ ...form, features: { ...(form.features || {}), [f.key]: e.target.checked } })} />
                  <span className="text-sm">{f.label}</span>
                </label>
              );
            })}
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button onClick={submit} data-testid="client-form-save" className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange"><Save className="w-3 h-3 inline mr-1" /> {editing ? "Atualizar" : "Criar cliente"}</button>
          {editing && <button onClick={cancel} className="text-sm px-4 py-2.5 rounded-full border border-black/15">Cancelar</button>}
        </div>
      </section>

      <div className="border border-black/10 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-black text-white text-xs uppercase tracking-wider">
            <tr><th className="p-3 text-left">Email</th><th className="p-3 text-left">Nome</th><th className="p-3 text-left">Empresa</th><th className="p-3 text-left">Atendimento</th><th className="p-3 text-left">Status</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-t border-black/5 hover:bg-black/[0.02]">
                <td className="p-3 font-mono text-xs">{c.email}</td>
                <td className="p-3">{c.name || "—"}</td>
                <td className="p-3">{c.company || "—"}</td>
                <td className="p-3 text-neutral-500">{c.responsible_am || "—"}</td>
                <td className="p-3"><span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${c.is_active === false ? "bg-red-500/10 text-red-500" : "bg-green-500/10 text-green-500"}`}>{c.is_active === false ? "Pausado" : "Ativo"}</span></td>
                <td className="p-3 text-right">
                  <button onClick={() => startEdit(c)} className="text-xs text-black hover:text-accent-orange">Editar</button>
                  <button onClick={() => del(c)} className="text-xs text-red-500 hover:text-red-700 ml-3"><Trash2 className="w-3 h-3 inline" /></button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-neutral-500">Nenhum cliente cadastrado.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (<div><label className="text-xs text-neutral-500">{label}</label>{children}</div>);
}
